import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import {
  ArrowDown, ArrowUp, Copy, Trash2, Plus, GripVertical, Type, Heading2, Image as ImageIcon,
  MousePointerClick, Quote, Minus, BarChart3, MoveVertical, Bold, Italic, List, Link as LinkIcon, Settings2, FileInput, Library,
} from "lucide-react";
import { MediaPicker } from "../MediaLibrary";
import { LEAD_DEFAULTS, LEAD_LABELS, encodeLead, decodeLead, type LeadKind } from "@/components/blog/LeadForm";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

/* ---------- Model ---------- */
export type BlockType = "heading" | "text" | "image" | "button" | "quote" | "divider" | "stat" | "spacer" | "leadform";
export type Block = { id: string; type: BlockType; data: Record<string, string> };
export type Column = { id: string; width: number; blocks: Block[] };
export type Section = {
  id: string;
  bg: "none" | "soft" | "gold" | "card" | "dark";
  pad: "s" | "m" | "l";
  align: "left" | "center";
  columns: Column[];
};
export type Doc = { v: 1; sections: Section[] };

const uid = () => Math.random().toString(36).slice(2, 10);
const esc = (s = "") => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const BLOCK_META: Record<BlockType, { label: string; icon: React.ReactNode; data: Record<string, string> }> = {
  heading: { label: "Título", icon: <Heading2 size={16} />, data: { text: "Novo título", level: "2" } },
  text: { label: "Texto", icon: <Type size={16} />, data: { html: "<p>Escreva aqui...</p>" } },
  image: { label: "Imagem", icon: <ImageIcon size={16} />, data: { src: "", alt: "", caption: "" } },
  button: { label: "Botão", icon: <MousePointerClick size={16} />, data: { label: "Fale com a equipe", url: "https://wa.me/5541988692568" } },
  quote: { label: "Citação", icon: <Quote size={16} />, data: { text: "Uma frase de impacto.", author: "" } },
  stat: { label: "Número", icon: <BarChart3 size={16} />, data: { value: "+120%", label: "de crescimento" } },
  divider: { label: "Linha", icon: <Minus size={16} />, data: {} },
  spacer: { label: "Espaço", icon: <MoveVertical size={16} />, data: { size: "m" } },
  leadform: { label: "Captura de lead", icon: <FileInput size={16} />, data: { cfg: encodeLead(LEAD_DEFAULTS.diag) } },
};

const newBlock = (type: BlockType): Block => ({ id: uid(), type, data: { ...BLOCK_META[type].data } });
const newSection = (widths: number[]): Section => ({
  id: uid(), bg: "none", pad: "m", align: "left",
  columns: widths.map((w) => ({ id: uid(), width: w, blocks: [newBlock("text")] })),
});

const LAYOUTS = [[1], [1, 1], [1, 2], [2, 1], [1, 1, 1], [1, 2, 1], [1, 1, 1, 1]];

/* ---------- Serialize ---------- */
const MARK = "<!--abb-builder:";
export const parseDoc = (html: string): Doc | null => {
  if (!html?.startsWith(MARK)) return null;
  try {
    const end = html.indexOf("-->");
    return JSON.parse(decodeURIComponent(html.slice(MARK.length, end)));
  } catch { return null; }
};

const blockHTML = (b: Block): string => {
  const d = b.data;
  switch (b.type) {
    case "heading": return `<h${d.level}>${esc(d.text)}</h${d.level}>`;
    case "text": return d.html;
    case "image": return d.src ? `<figure class="blog-figure"><img src="${esc(d.src)}" alt="${esc(d.alt)}" loading="lazy">${d.caption ? `<figcaption>${esc(d.caption)}</figcaption>` : ""}</figure>` : "";
    case "button": return `<p class="blog-btn-wrap"><a class="blog-btn" href="${esc(d.url)}" target="_blank" rel="noopener">${esc(d.label)}</a></p>`;
    case "quote": return `<blockquote><p>${esc(d.text)}</p>${d.author ? `<cite>— ${esc(d.author)}</cite>` : ""}</blockquote>`;
    case "stat": return `<div class="blog-stat"><strong>${esc(d.value)}</strong><span>${esc(d.label)}</span></div>`;
    case "divider": return "<hr>";
    case "spacer": return `<div class="blog-spacer" data-size="${d.size}"></div>`;
    case "leadform": return `<div class="blog-leadform" data-leadform="${esc(d.cfg)}"></div>`;
  }
};

export const serializeDoc = (doc: Doc): string => {
  const body = doc.sections.map((s) => {
    const cols = s.columns.map((c) => `${c.width}fr`).join(" ");
    const inner = s.columns.map((c) => `<div data-type="column" class="blog-col">${c.blocks.map(blockHTML).join("")}</div>`).join("");
    return `<section class="blog-section" data-bg="${s.bg}" data-pad="${s.pad}" data-align="${s.align}"><div data-type="columns" class="blog-grid" data-cols="${cols}" style="--cols: ${cols}">${inner}</div></section>`;
  }).join("");
  return `${MARK}${encodeURIComponent(JSON.stringify(doc))}-->${body}`;
};

const B = (type: BlockType, data: Record<string, string> = {}): Block => ({ id: uid(), type, data: { ...BLOCK_META[type].data, ...data } });
const S = (cols: Block[][], opts: Partial<Omit<Section, "columns" | "id">> = {}, widths?: number[]): Section => ({
  id: uid(), bg: "none", pad: "m", align: "left", ...opts,
  columns: cols.map((blocks, i) => ({ id: uid(), width: widths?.[i] ?? 1, blocks })),
});
const T = (h: string) => B("text", { html: h });
const lead = (k: LeadKind) => B("leadform", { cfg: encodeLead(LEAD_DEFAULTS[k]) });
const WA = "https://wa.me/5541988692568";

export const LAYOUT_SUGGESTIONS: { key: string; name: string; desc: string; build: () => Doc }[] = [
  {
    key: "case", name: "Estudo de caso / Resultados", desc: "Números em destaque, problema, solução, depoimento e CTA",
    build: () => ({ v: 1, sections: [
      S([[B("stat", { value: "+180%", label: "em vendas" })], [B("stat", { value: "3x", label: "mais leads" })], [B("stat", { value: "-40%", label: "custo por lead" })]], { bg: "gold", align: "center" }),
      S([[B("heading", { text: "O desafio" }), T("<p>Descreva o cenário do cliente antes do projeto.</p>")], [B("heading", { text: "A solução" }), T("<p>O que implementamos: estratégia, conteúdo, automações e IA.</p>")]]),
      S([[B("quote", { text: "Depoimento do cliente sobre os resultados.", author: "Nome, Empresa" })]], { bg: "soft", align: "center" }),
      S([[B("heading", { text: "Quer resultados assim na sua marca?" }), B("button", { label: "Falar com especialista", url: WA })]], { bg: "card", align: "center" }),
    ] }),
  },
  {
    key: "guide", name: "Guia prático passo a passo", desc: "Introdução, passos numerados, alerta e formulário no meio",
    build: () => ({ v: 1, sections: [
      S([[T("<p>Introdução curta: o que o leitor vai aprender e por que isso importa.</p>"), T("<ol><li>Passo 1</li><li>Passo 2</li><li>Passo 3</li></ol>")]]),
      S([[B("heading", { text: "Passo 1 — Título" }), T("<p>Explique o primeiro passo.</p>")]]),
      S([[B("heading", { text: "Atenção", level: "3" }), T("<p>Erro comum que o leitor deve evitar.</p>")]], { bg: "gold" }),
      S([[lead("material")]]),
      S([[B("heading", { text: "Passo 2 — Título" }), T("<p>Explique o segundo passo.</p>")]]),
      S([[B("heading", { text: "Passo 3 — Título" }), T("<p>Explique o terceiro passo e conclua.</p>")]]),
    ] }),
  },
  {
    key: "opinion", name: "Liderança & opinião", desc: "Manifesto, 3 pilares, citação de autoridade e convite",
    build: () => ({ v: 1, sections: [
      S([[B("heading", { text: "Nosso manifesto" }), T("<p>Abra com uma tese forte e provocativa sobre o mercado.</p>")]], { pad: "l", align: "center" }),
      S([[B("heading", { text: "Pilar 1", level: "3" }), T("<p>Argumento.</p>")], [B("heading", { text: "Pilar 2", level: "3" }), T("<p>Argumento principal, mais desenvolvido.</p>")], [B("heading", { text: "Pilar 3", level: "3" }), T("<p>Argumento.</p>")]], {}, [1, 2, 1]),
      S([[B("quote", { text: "Uma frase de autoridade que resume a visão.", author: "Fundador(a), Abillyting" })]], { bg: "dark", align: "center", pad: "l" }),
      S([[B("heading", { text: "Vamos conversar sobre sua marca?" }), B("button", { label: "Agendar reunião", url: WA })]], { bg: "card", align: "center" }),
    ] }),
  },
  {
    key: "compare", name: "Comparativo de soluções", desc: "Antes x Depois (ou Prós x Contras) e recomendação",
    build: () => ({ v: 1, sections: [
      S([[T("<p>Contextualize a comparação: o que está em jogo para o leitor.</p>")]]),
      S([[B("heading", { text: "Antes", level: "3" }), T("<ul><li>Ponto 1</li><li>Ponto 2</li><li>Ponto 3</li></ul>")], [B("heading", { text: "Depois", level: "3" }), T("<ul><li>Ponto 1</li><li>Ponto 2</li><li>Ponto 3</li></ul>")]], { bg: "soft" }),
      S([[B("heading", { text: "Recomendação do especialista" }), T("<p>Qual caminho escolher e por quê.</p>")]], { bg: "gold" }),
      S([[lead("diag")]]),
    ] }),
  },
];

export const emptyDoc = (): Doc => ({ v: 1, sections: [newSection([1])] });

/* ---------- Rich text block ---------- */
const RichText = ({ html, onChange }: { html: string; onChange: (h: string) => void }) => {
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [3] } }), Link.configure({ openOnClick: false })],
    content: html,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: { attributes: { class: "prose prose-sm max-w-none focus:outline-none min-h-[2.5rem]" } },
  });
  const btn = (fn: () => void, icon: React.ReactNode, active?: boolean) => (
    <button type="button" onMouseDown={(e) => { e.preventDefault(); fn(); }} className={`p-1 rounded hover:bg-muted ${active ? "text-primary bg-muted" : "text-muted-foreground"}`}>{icon}</button>
  );
  return (
    <div className="group/rt">
      <div className="flex gap-0.5 mb-1 opacity-0 group-focus-within/rt:opacity-100 transition-opacity">
        {btn(() => editor?.chain().focus().toggleBold().run(), <Bold size={13} />, editor?.isActive("bold"))}
        {btn(() => editor?.chain().focus().toggleItalic().run(), <Italic size={13} />, editor?.isActive("italic"))}
        {btn(() => editor?.chain().focus().toggleBulletList().run(), <List size={13} />, editor?.isActive("bulletList"))}
        {btn(() => { const u = window.prompt("URL do link:"); if (u) editor?.chain().focus().setLink({ href: u }).run(); }, <LinkIcon size={13} />, editor?.isActive("link"))}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

/* ---------- Block editor ---------- */
const field = "w-full bg-transparent border-0 border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1";

const BlockEditor = ({ block, onChange }: { block: Block; onChange: (d: Record<string, string>) => void }) => {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [lib, setLib] = useState(false);
  const d = block.data;
  const set = (k: string, v: string) => onChange({ ...d, [k]: v });

  const upload = async (file: File) => {
    const path = `inline/${Date.now()}-${uid()}.${file.name.split(".").pop() ?? "jpg"}`;
    const { error } = await supabase.storage.from("blog-covers").upload(path, file);
    if (error) return toast({ title: "Erro no upload", description: error.message, variant: "destructive" });
    const { data } = await supabase.storage.from("blog-covers").createSignedUrl(path, 60 * 60 * 24 * 365 * 10);
    if (data?.signedUrl) set("src", data.signedUrl);
  };

  switch (block.type) {
    case "heading":
      return (
        <div className="flex items-center gap-2">
          <select value={d.level} onChange={(e) => set("level", e.target.value)} className="text-xs bg-muted rounded px-1 py-0.5">
            <option value="2">H2</option><option value="3">H3</option>
          </select>
          <input value={d.text} onChange={(e) => set("text", e.target.value)} className={`${field} font-heading font-bold ${d.level === "2" ? "text-2xl" : "text-lg"}`} />
        </div>
      );
    case "text":
      return <RichText html={d.html} onChange={(h) => set("html", h)} />;
    case "image":
      return (
        <div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
          {d.src ? (
            <button type="button" onClick={() => fileRef.current?.click()} className="block w-full"><img src={d.src} alt={d.alt} className="w-full rounded-lg" /></button>
          ) : (
            <button type="button" onClick={() => fileRef.current?.click()} className="w-full aspect-video rounded-lg border-2 border-dashed border-border hover:border-primary hover:text-primary text-muted-foreground flex flex-col items-center justify-center gap-2 transition-colors">
              <ImageIcon size={28} /><span className="text-sm">Enviar imagem</span>
            </button>
          )}
          <button type="button" onClick={() => setLib(true)} className="mt-1 w-full flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-primary"><Library size={12} /> Escolher da biblioteca</button>
          <MediaPicker open={lib} onOpenChange={setLib} onPick={(m) => set("src", m.url)} />
          <input value={d.caption} onChange={(e) => set("caption", e.target.value)} placeholder="Legenda (opcional)" className={`${field} text-xs text-center text-muted-foreground mt-1`} />
        </div>
      );
    case "button":
      return (
        <div className="space-y-1">
          <span className="inline-block px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold">
            <input value={d.label} onChange={(e) => set("label", e.target.value)} className="bg-transparent focus:outline-none text-center" size={Math.max(8, d.label.length)} />
          </span>
          <input value={d.url} onChange={(e) => set("url", e.target.value)} placeholder="https://..." className={`${field} text-xs text-muted-foreground`} />
        </div>
      );
    case "quote":
      return (
        <div className="border-l-4 border-primary pl-4">
          <textarea value={d.text} onChange={(e) => set("text", e.target.value)} rows={2} className={`${field} italic text-lg resize-none`} />
          <input value={d.author} onChange={(e) => set("author", e.target.value)} placeholder="Autor (opcional)" className={`${field} text-sm text-muted-foreground`} />
        </div>
      );
    case "stat":
      return (
        <div className="text-center">
          <input value={d.value} onChange={(e) => set("value", e.target.value)} className={`${field} text-center text-4xl font-heading font-bold text-primary`} />
          <input value={d.label} onChange={(e) => set("label", e.target.value)} className={`${field} text-center text-sm text-muted-foreground`} />
        </div>
      );
    case "divider":
      return <hr className="border-border my-2" />;
    case "spacer":
      return (
        <div className="flex items-center justify-center gap-1 border border-dashed border-border rounded text-xs text-muted-foreground" style={{ height: d.size === "s" ? 24 : d.size === "l" ? 72 : 44 }}>
          {(["s", "m", "l"] as const).map((s) => (
            <button key={s} type="button" onClick={() => set("size", s)} className={`px-2 rounded ${d.size === s ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{s.toUpperCase()}</button>
          ))}
        </div>
      );
    case "leadform": {
      const cfg = decodeLead(d.cfg) ?? LEAD_DEFAULTS.diag;
      const setCfg = (patch: Partial<typeof cfg>) => set("cfg", encodeLead({ ...cfg, ...patch }));
      return (
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-3 space-y-2">
          <div className="flex gap-1 flex-wrap">
            {(Object.keys(LEAD_LABELS) as LeadKind[]).map((k) => (
              <button key={k} type="button" onClick={() => set("cfg", encodeLead({ ...LEAD_DEFAULTS[k] }))} className={`px-2 py-0.5 rounded-full text-xs ${cfg.kind === k ? "bg-primary text-primary-foreground" : "bg-background border border-border hover:border-primary"}`}>{LEAD_LABELS[k]}</button>
            ))}
          </div>
          <input value={cfg.title} onChange={(e) => setCfg({ title: e.target.value })} className={`${field} font-heading font-bold`} />
          <input value={cfg.text} onChange={(e) => setCfg({ text: e.target.value })} placeholder="Texto de apoio" className={`${field} text-sm text-muted-foreground`} />
          <p className="text-[11px] text-muted-foreground">Campos: {cfg.kind === "diag" ? "nome, e-mail e segmento" : cfg.kind === "material" ? "nome e e-mail" : "e-mail ou WhatsApp"}</p>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold">
              <input value={cfg.button} onChange={(e) => setCfg({ button: e.target.value })} className="bg-transparent focus:outline-none" size={Math.max(8, cfg.button.length)} />
            </span>
          </div>
          {cfg.kind === "material" && (
            <input value={cfg.link ?? ""} onChange={(e) => setCfg({ link: e.target.value })} placeholder="Link do material (liberado após o envio)" className={`${field} text-xs`} />
          )}
        </div>
      );
    }
  }
};

/* ---------- Builder ---------- */
type DragRef = { kind: "section"; from: number } | { kind: "block"; s: number; c: number; b: number } | { kind: "new"; type: BlockType } | null;

const BG_OPTS: { v: Section["bg"]; label: string; cls: string }[] = [
  { v: "none", label: "Sem fundo", cls: "bg-background" },
  { v: "soft", label: "Suave", cls: "bg-muted" },
  { v: "gold", label: "Dourado", cls: "bg-primary/15" },
  { v: "card", label: "Card", cls: "bg-card shadow" },
  { v: "dark", label: "Escuro", cls: "bg-foreground" },
];

const PageBuilder = ({ doc, onChange }: { doc: Doc; onChange: (d: Doc) => void }) => {
  const drag = useRef<DragRef>(null);
  const [openSettings, setOpenSettings] = useState<string | null>(null);
  const [dropHint, setDropHint] = useState<string | null>(null);
  const [addMenu, setAddMenu] = useState<string | null>(null);

  useEffect(() => { const h = () => setAddMenu(null); window.addEventListener("click", h); return () => window.removeEventListener("click", h); }, []);

  const update = (fn: (s: Section[]) => void) => {
    const sections: Section[] = structuredClone(doc.sections);
    fn(sections);
    onChange({ ...doc, sections });
  };
  const move = <T,>(arr: T[], from: number, to: number) => { const [x] = arr.splice(from, 1); arr.splice(to, 0, x); };
  const cloneBlock = (b: Block): Block => ({ ...structuredClone(b), id: uid() });
  const cloneSection = (s: Section): Section => ({ ...structuredClone(s), id: uid(), columns: s.columns.map((c) => ({ ...c, id: uid(), blocks: c.blocks.map(cloneBlock) })) });

  const setLayout = (si: number, widths: number[]) => update((ss) => {
    const s = ss[si];
    const all = s.columns.flatMap((c) => c.blocks);
    const cols: Column[] = widths.map((w, i) => ({ id: s.columns[i]?.id ?? uid(), width: w, blocks: s.columns[i]?.blocks ?? [newBlock("text")] }));
    // blocks from removed columns go to the last column
    if (widths.length < s.columns.length) cols[cols.length - 1].blocks.push(...s.columns.slice(widths.length).flatMap((c) => c.blocks));
    void all;
    s.columns = cols;
  });

  const dropOnColumn = (si: number, ci: number, bi: number) => {
    const d = drag.current;
    setDropHint(null);
    if (!d || d.kind === "section") return;
    update((ss) => {
      const target = ss[si].columns[ci].blocks;
      if (d.kind === "new") target.splice(bi, 0, newBlock(d.type));
      else {
        const [blk] = ss[d.s].columns[d.c].blocks.splice(d.b, 1);
        const adj = d.s === si && d.c === ci && d.b < bi ? bi - 1 : bi;
        target.splice(adj, 0, blk);
      }
    });
    drag.current = null;
  };

  const DropZone = ({ si, ci, bi }: { si: number; ci: number; bi: number }) => {
    const key = `${si}-${ci}-${bi}`;
    return (
      <div
        onDragOver={(e) => { if (drag.current?.kind !== "section") { e.preventDefault(); setDropHint(key); } }}
        onDragLeave={() => setDropHint((h) => (h === key ? null : h))}
        onDrop={(e) => { e.preventDefault(); dropOnColumn(si, ci, bi); }}
        className={`transition-all rounded ${dropHint === key ? "h-10 bg-primary/15 border-2 border-dashed border-primary" : "h-2"}`}
      />
    );
  };

  return (
    <div className="space-y-4">
      {/* Block palette */}
      <div className="sticky top-2 z-20 flex flex-wrap items-center gap-1 rounded-xl border border-border bg-background/95 backdrop-blur p-2 shadow-sm">
        {(Object.keys(BLOCK_META) as BlockType[]).map((t) => (
          <div
            key={t}
            draggable
            onDragStart={(e) => { drag.current = { kind: "new", type: t }; e.dataTransfer.effectAllowed = "copy"; e.dataTransfer.setData("text/plain", t); }}
            onDragEnd={() => setDropHint(null)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm cursor-grab active:cursor-grabbing hover:bg-primary/10 hover:text-primary transition-colors select-none"
            title="Arraste para uma coluna"
          >
            {BLOCK_META[t].icon}{BLOCK_META[t].label}
          </div>
        ))}
      </div>

      {doc.sections.map((s, si) => (
        <div
          key={s.id}
          onDragOver={(e) => { if (drag.current?.kind === "section") { e.preventDefault(); setDropHint(`sec-${si}`); } }}
          onDrop={(e) => {
            const d = drag.current;
            if (d?.kind !== "section") return;
            e.preventDefault(); setDropHint(null);
            update((ss) => move(ss, d.from, si));
            drag.current = null;
          }}
          className={`group/sec relative rounded-xl border-2 transition-colors ${dropHint === `sec-${si}` ? "border-primary" : "border-transparent hover:border-primary/30"}`}
        >
          {/* Section toolbar */}
          <div className="absolute -top-3 left-3 right-3 z-10 flex items-center justify-between opacity-0 group-hover/sec:opacity-100 focus-within:opacity-100 transition-opacity">
            <div className="flex items-center gap-1 rounded-full bg-background border border-border shadow-sm px-1">
              <span
                draggable
                onDragStart={(e) => { drag.current = { kind: "section", from: si }; e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", "s"); }}
                className="p-1 cursor-grab text-muted-foreground" title="Arrastar seção"
              ><GripVertical size={14} /></span>
              {LAYOUTS.map((l) => {
                const active = l.join() === s.columns.map((c) => c.width).join();
                return (
                  <button key={l.join()} type="button" onClick={() => setLayout(si, l)} title={`${l.length} coluna(s)`} className={`flex gap-px h-5 w-9 p-0.5 rounded ${active ? "bg-primary/15" : "hover:bg-muted"}`}>
                    {l.map((w, i) => <span key={i} style={{ flex: w }} className={`rounded-sm ${active ? "bg-primary" : "bg-muted-foreground/40"}`} />)}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-0.5 rounded-full bg-background border border-border shadow-sm px-1">
              <button type="button" title="Estilo" onClick={() => setOpenSettings(openSettings === s.id ? null : s.id)} className="p-1.5 hover:text-primary"><Settings2 size={14} /></button>
              <button type="button" title="Subir" disabled={si === 0} onClick={() => update((ss) => move(ss, si, si - 1))} className="p-1.5 hover:text-primary disabled:opacity-30"><ArrowUp size={14} /></button>
              <button type="button" title="Descer" disabled={si === doc.sections.length - 1} onClick={() => update((ss) => move(ss, si, si + 1))} className="p-1.5 hover:text-primary disabled:opacity-30"><ArrowDown size={14} /></button>
              <button type="button" title="Duplicar" onClick={() => update((ss) => ss.splice(si + 1, 0, cloneSection(s)))} className="p-1.5 hover:text-primary"><Copy size={14} /></button>
              <button type="button" title="Excluir" onClick={() => update((ss) => ss.splice(si, 1))} className="p-1.5 hover:text-destructive"><Trash2 size={14} /></button>
            </div>
          </div>

          {openSettings === s.id && (
            <div className="absolute top-5 right-3 z-30 w-64 rounded-xl border border-border bg-background shadow-lg p-3 space-y-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Fundo</p>
                <div className="flex gap-1.5">
                  {BG_OPTS.map((o) => (
                    <button key={o.v} type="button" title={o.label} onClick={() => update((ss) => { ss[si].bg = o.v; })} className={`h-7 w-7 rounded-full border ${o.cls} ${s.bg === o.v ? "ring-2 ring-primary ring-offset-1" : "border-border"}`} />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Espaçamento</p>
                <div className="flex gap-1">
                  {([["s", "Compacto"], ["m", "Normal"], ["l", "Amplo"]] as const).map(([v, l]) => (
                    <button key={v} type="button" onClick={() => update((ss) => { ss[si].pad = v; })} className={`flex-1 py-1 rounded text-xs ${s.pad === v ? "bg-primary text-primary-foreground" : "bg-muted"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Alinhamento</p>
                <div className="flex gap-1">
                  {([["left", "Esquerda"], ["center", "Centro"]] as const).map(([v, l]) => (
                    <button key={v} type="button" onClick={() => update((ss) => { ss[si].align = v; })} className={`flex-1 py-1 rounded text-xs ${s.align === v ? "bg-primary text-primary-foreground" : "bg-muted"}`}>{l}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Section canvas (uses public styles for true preview) */}
          <section className="blog-section !my-0" data-bg={s.bg} data-pad={s.pad} data-align={s.align}>
            <div className="grid gap-4" style={{ gridTemplateColumns: s.columns.map((c) => `${c.width}fr`).join(" ") }}>
              {s.columns.map((c, ci) => (
                <div key={c.id} className="min-w-0 rounded-lg border border-dashed border-border/70 p-2">
                  <DropZone si={si} ci={ci} bi={0} />
                  {c.blocks.map((b, bi) => (
                    <div key={b.id}>
                      <div className="group/blk relative rounded-md hover:bg-muted/30 px-1">
                        <div className="absolute -left-1 top-0 -translate-x-full flex flex-col opacity-0 group-hover/blk:opacity-100 transition-opacity">
                          <span
                            draggable
                            onDragStart={(e) => { drag.current = { kind: "block", s: si, c: ci, b: bi }; e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", "b"); }}
                            onDragEnd={() => setDropHint(null)}
                            className="p-0.5 cursor-grab text-muted-foreground hover:text-primary" title="Arrastar bloco"
                          ><GripVertical size={14} /></span>
                        </div>
                        <div className="absolute right-0 -top-2 z-10 flex gap-0.5 rounded bg-background border border-border shadow-sm opacity-0 group-hover/blk:opacity-100 transition-opacity">
                          <button type="button" title="Duplicar" onClick={() => update((ss) => ss[si].columns[ci].blocks.splice(bi + 1, 0, cloneBlock(b)))} className="p-1 hover:text-primary"><Copy size={12} /></button>
                          <button type="button" title="Excluir" onClick={() => update((ss) => ss[si].columns[ci].blocks.splice(bi, 1))} className="p-1 hover:text-destructive"><Trash2 size={12} /></button>
                        </div>
                        <BlockEditor block={b} onChange={(data) => update((ss) => { ss[si].columns[ci].blocks[bi].data = data; })} />
                      </div>
                      <DropZone si={si} ci={ci} bi={bi + 1} />
                    </div>
                  ))}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setAddMenu(addMenu === c.id ? null : c.id); }}
                      className="w-full flex items-center justify-center gap-1 py-1.5 text-xs text-muted-foreground hover:text-primary rounded opacity-60 hover:opacity-100"
                    ><Plus size={14} /> Bloco</button>
                    {addMenu === c.id && (
                      <div className="absolute left-1/2 -translate-x-1/2 top-full z-30 grid grid-cols-2 gap-1 w-56 p-2 rounded-xl border border-border bg-background shadow-lg">
                        {(Object.keys(BLOCK_META) as BlockType[]).map((t) => (
                          <button key={t} type="button" onClick={() => update((ss) => ss[si].columns[ci].blocks.push(newBlock(t)))} className="flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-primary/10 hover:text-primary">
                            {BLOCK_META[t].icon}{BLOCK_META[t].label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      ))}

      {/* Add section */}
      <div className="rounded-xl border-2 border-dashed border-border p-4 text-center">
        <p className="text-xs text-muted-foreground mb-2">Adicionar seção</p>
        <div className="flex flex-wrap justify-center gap-2">
          {LAYOUTS.map((l) => (
            <button key={l.join()} type="button" onClick={() => update((ss) => ss.push(newSection(l)))} className="flex gap-0.5 h-9 w-16 p-1 rounded-lg border border-border hover:border-primary group/l">
              {l.map((w, i) => <span key={i} style={{ flex: w }} className="rounded bg-muted-foreground/30 group-hover/l:bg-primary/60" />)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PageBuilder;
